import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { resolveMoney } from "@/utils/formatPrice";
// TASK-66 PR-D: fetchTeamIdsForUser unions teams.player1/2 +
// team_members.user_id (was UPPDRAG-62 PR-D's three-source union;
// captain_id stripped — captain users still resolve via team_members).
import { fetchTeamIdsForUser } from "@/lib/teamMembershipQueries";

// ──────────────────────────────── Types ────────────────────────────────

export interface CustomerDetailStats {
  totalBookings: number;
  cancellations: number;
  totalSpent: number;            // kronor (rendered via formatPrice legacy form)
  teamsCount: number;
  noShows: number;
  unpaidAmount: number;          // kronor
  lastActive: string | null;
  cancelRate: number;
}

export interface CustomerMembership {
  id: string;
  status: string;
  plan_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  // UPPDRAG-customer-card-hotfix: `canceled_at` was selected from
  // `memberships`, but the column does not exist in PROD schema. PostgREST
  // returned 400 (SQLSTATE 42703) → React Query swallowed the throw → the
  // memberships card rendered "Inga aktiva medlemskap" regardless of
  // actual rows. Same shape as the PR-99 `bookings.organization_id` bug.
  // Memberships have `current_period_end` (a future timestamp for active,
  // a past timestamp once the period rolls off); no separate cancellation
  // timestamp exists. Drop both the field and the SELECT below.
  // Sprint D PR-AE: membership_plans includes Phase 4 _minor + currency for dual-state reads.
  membership_plans: { name: string; price: number; price_minor: number | null; currency: string; interval: string } | null;
}

export interface CustomerBookingRow {
  id: string;
  user_id: string;
  court_id: string;
  start_time: string;
  end_time: string;
  status: string;
  // UPPDRAG-customer-card-hotfix: `booking_type` was selected from
  // `bookings`, but the column does not exist in PROD schema. PostgREST
  // returned 400 (SQLSTATE 42703) → React Query swallowed the throw → the
  // customer card's Bokningar + Översikt tabs rendered empty despite the
  // KPI count showing 21 bookings (the count query uses `select("*")` so
  // it succeeds). Same shape as PR-99's `bookings.organization_id` bug.
  // Bookings have a "type" *concept* spread across `is_open_match`,
  // `activity_id`, and cross-table FKs (lessons, activities, leagues),
  // but no single `booking_type` column. Quick path: drop the field +
  // label all rows "Bokning" until a derived helper lands.
  price_total: number | null;
  // Sprint D PR-AE Phase 4 dual-state cols (öre + currency).
  price_total_minor?: number | null;
  currency?: string;
  courts: { name: string } | null;
  booking_payments: { amount: number; amount_minor: number | null; currency: string; status: string }[] | null;
}

export interface CustomerPaymentRow {
  id: string;
  amount: number;                      // legacy kronor
  amount_minor: number | null;         // öre (Sprint D)
  currency: string;
  status: string;
  payment_method: string | null;
  description: string | null;
  type: string | null;
  stripe_payment_id: string | null;
  created_at: string;
  reference_id: string | null;
}

export interface CustomerNoteRow {
  id: string;
  content: string;
  created_at: string;
  author_id: string;
  profiles: { full_name: string | null } | null;
}

// ──────────────────────────────── Hook ────────────────────────────────

export function useCustomerDetailData(
  customerId: string | undefined,
  userId: string | null | undefined,
  organizationId: string | undefined,
) {
  const queryClient = useQueryClient();

  // ── Quick stats (live) ──
  const statsQuery = useQuery({
    queryKey: ["customer", customerId, "stats"],
    queryFn: async () => {
      // Bookings count (confirmed/completed)
      const { count: bookingsCount } = await supabase
        .from("bookings")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId!)
        .in("status", ["confirmed", "completed"]);

      // Cancellations count
      const { count: cancellationsCount } = await supabase
        .from("bookings")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId!)
        .eq("status", "cancelled");

      // Total spent — Sprint D aggregates cutover: prefer amount_minor.
      const { data: spentData } = await supabase
        .from("booking_payments" as any)
        .select("amount, amount_minor, currency")
        .eq("user_id", userId!)
        .eq("status", "completed");
      const spentRows = (spentData as { amount: number; amount_minor: number | null; currency: string }[]) ?? [];
      const totalSpentMinor = spentRows.reduce(
        (sum, r) => sum + (resolveMoney(r.amount_minor, r.amount, r.currency)?.valueMinor ?? 0),
        0,
      );
      const totalSpent = Math.round(totalSpentMinor / 100);  // kronor for legacy CustomerDetailStats interface

      // Teams count
      // TASK-66 PR-D: union teams.player1/2 ∪ team_members.user_id (captain
      // users resolve via team_members branch; captain_id stripped).
      const teamIds = userId ? await fetchTeamIdsForUser(supabase, userId) : [];
      const teamsCount = teamIds.length;

      // No-shows
      const { count: noShowCount } = await supabase
        .from("no_show_records")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId!);

      // Unpaid amount — Sprint D aggregates cutover.
      const { data: unpaidData } = await supabase
        .from("booking_payments" as any)
        .select("amount, amount_minor, currency")
        .eq("user_id", userId!)
        .not("status", "in", '("paid","manual","completed")');
      const unpaidRows = (unpaidData as { amount: number; amount_minor: number | null; currency: string }[]) ?? [];
      const unpaidAmountMinor = unpaidRows.reduce(
        (sum, r) => sum + (resolveMoney(r.amount_minor, r.amount, r.currency)?.valueMinor ?? 0), 0);
      const unpaidAmount = Math.round(unpaidAmountMinor / 100);

      // Last active (most recent booking)
      const { data: lastBooking } = await supabase
        .from("bookings")
        .select("start_time")
        .eq("user_id", userId!)
        .order("start_time", { ascending: false })
        .limit(1)
        .maybeSingle();

      return {
        totalBookings: bookingsCount ?? 0,
        cancellations: cancellationsCount ?? 0,
        totalSpent,
        teamsCount,
        noShows: noShowCount ?? 0,
        unpaidAmount,
        lastActive: lastBooking?.start_time ?? null,
        cancelRate: (bookingsCount ?? 0) > 0 ? Math.round(((cancellationsCount ?? 0) / ((bookingsCount ?? 0) + (cancellationsCount ?? 0))) * 100) : 0,
      } satisfies CustomerDetailStats;
    },
    enabled: !!customerId && !!userId,
  });

  // ── Memberships ──
  // Sprint D PR-AE: include membership_plans price_minor + currency for Phase 4-aware reads.
  const membershipsQuery = useQuery({
    queryKey: ["customer", customerId, "memberships"],
    queryFn: async () => {
      // UPPDRAG-customer-card-hotfix: dropped `canceled_at` from SELECT —
      // column doesn't exist in PROD schema, was returning 400.
      const { data, error } = await supabase
        .from("memberships" as any)
        .select("id, status, plan_id, current_period_start, current_period_end, membership_plans(name, price, price_minor, currency, interval)")
        .eq("user_id", userId!)
        .order("current_period_start", { ascending: false });
      if (error) throw error;
      return (data ?? []) as CustomerMembership[];
    },
    enabled: !!customerId && !!userId,
  });

  // ── Upcoming bookings ──
  // Sprint D PR-AE: include price_total_minor + currency on bookings, and amount_minor + currency on payments
  // for downstream Phase 4-aware reads in CustomerUpcomingBookings + tabs/OverviewTab.
  const upcomingBookingsQuery = useQuery({
    queryKey: ["customer", customerId, "upcoming-bookings"],
    queryFn: async () => {
      // UPPDRAG-customer-card-hotfix: dropped `booking_type` from SELECT —
      // column doesn't exist in PROD schema, was returning 400.
      const { data, error } = await supabase
        .from("bookings")
        .select("id, user_id, court_id, start_time, end_time, status, price_total, price_total_minor, currency, courts(name), booking_payments(amount, amount_minor, currency, status)")
        .eq("user_id", userId!)
        .gt("start_time", new Date().toISOString())
        .neq("status", "cancelled")
        .order("start_time", { ascending: true })
        .limit(5);
      if (error) throw error;
      return (data ?? []) as CustomerBookingRow[];
    },
    enabled: !!customerId && !!userId,
  });

  // ── Booking history (paginated) ──
  function useBookingHistory(page: number) {
    return useQuery({
      queryKey: ["customer", customerId, "booking-history", page],
      queryFn: async () => {
        const pageSize = 10;
        const from = page * pageSize;
        const to = from + pageSize - 1;

        // Sprint D PR-AE: include _minor + currency for Phase 4-aware reads in CustomerBookingHistory + tabs/BookingsTab.
        // UPPDRAG-customer-card-hotfix: dropped `booking_type` from SELECT —
        // column doesn't exist in PROD schema, was returning 400.
        const { data, error, count } = await supabase
          .from("bookings")
          .select(
            "id, user_id, court_id, start_time, end_time, status, price_total, price_total_minor, currency, courts(name), booking_payments(amount, amount_minor, currency, status)",
            { count: "exact" },
          )
          .eq("user_id", userId!)
          .lte("start_time", new Date().toISOString())
          .order("start_time", { ascending: false })
          .range(from, to);
        if (error) throw error;
        return {
          bookings: (data ?? []) as CustomerBookingRow[],
          totalCount: count ?? 0,
          totalPages: Math.ceil((count ?? 0) / pageSize),
        };
      },
      enabled: !!customerId && !!userId,
    });
  }

  // ── Payment history (paginated) ──
  function usePaymentHistory(page: number) {
    return useQuery({
      queryKey: ["customer", customerId, "payments", page],
      queryFn: async () => {
        const pageSize = 10;
        const from = page * pageSize;
        const to = from + pageSize - 1;

        const { data, error, count } = await supabase
          .from("credit_transactions" as any)
          .select("*", { count: "exact" })
          .eq("user_id", userId!)
          .eq("organization_id", organizationId!)
          .order("created_at", { ascending: false })
          .range(from, to);
        if (error) throw error;
        const rows = (data ?? []) as CustomerPaymentRow[];
        return {
          payments: rows,
          totalCount: count ?? 0,
          totalPages: Math.ceil((count ?? 0) / pageSize),
        };
      },
      enabled: !!customerId && !!userId && !!organizationId,
    });
  }

  // ── Notes ──
  // UPPDRAG-20: customer_notes_author_id_profiles_fkey added in migration
  // 20260428120000 makes the `profiles:author_id` embed work. Before that
  // FK existed, this query returned HTTP 400 silently — same root cause as
  // the dashboard "Obetalda" 0-vs-53 bug. Notes UI rendered empty regardless
  // of actual data. The `as any` on `.from("customer_notes")` is also
  // dropped — table is in the auto-generated supabase types.
  const notesQuery = useQuery({
    queryKey: ["customer", customerId, "notes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customer_notes")
        .select("id, content, created_at, author_id, profiles:author_id(full_name)")
        .eq("customer_id", customerId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as CustomerNoteRow[];
    },
    enabled: !!customerId,
  });

  const addNoteMutation = useMutation({
    mutationFn: async ({ content }: { content: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Ej inloggad");
      const { error } = await supabase
        .from("customer_notes")
        .insert({
          organization_id: organizationId!,
          customer_id: customerId!,
          author_id: user.id,
          content: content.trim(),
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer", customerId, "notes"] });
    },
  });

  // ── Wallet balance — Sprint D dual-state read ──
  const walletQuery = useQuery({
    queryKey: ["customer", customerId, "wallet"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("player_credits" as any)
        .select("balance, balance_minor, currency")
        .eq("user_id", userId!)
        .eq("organization_id", organizationId!)
        .maybeSingle();
      if (error) throw error;
      const row = data as { balance: number; balance_minor: number | null; currency: string } | null;
      // Prefer _minor; fall back to legacy. Returns kronor for backwards-compat
      // with consumers that pass to formatPrice(number) legacy form.
      const money = resolveMoney(row?.balance_minor, row?.balance, row?.currency);
      return money ? Math.round(money.valueMinor / 100) : 0;
    },
    enabled: !!customerId && !!userId && !!organizationId,
  });

  // ── Punch cards ──
  const punchCardsQuery = useQuery({
    queryKey: ["customer", customerId, "punch-cards"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("punch_cards" as any)
        .select("*")
        .eq("user_id", userId!)
        .eq("organization_id", organizationId!)
        .eq("status", "active");
      if (error) throw error;
      return (data ?? []) as {
        id: string;
        name: string;
        total_punches: number;
        used_punches: number;
        expires_at: string | null;
      }[];
    },
    enabled: !!customerId && !!userId && !!organizationId,
  });

  // ── Block / unblock ──
  const toggleBlockMutation = useMutation({
    mutationFn: async (blocked: boolean) => {
      const { error } = await supabase
        .from("customers")
        .update({ blocked } as Record<string, unknown>)
        .eq("id", customerId!);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer", customerId] });
      queryClient.invalidateQueries({ queryKey: ["admin-customers"] });
      queryClient.invalidateQueries({ queryKey: ["customer-mgmt"] });
    },
  });

  // ── Delete (soft) ──
  const deleteCustomerMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("customers")
        .update({ is_archived: true })
        .eq("id", customerId!);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-customers"] });
      queryClient.invalidateQueries({ queryKey: ["customer-mgmt"] });
    },
  });

  return {
    // Stats
    stats: statsQuery.data ?? null,
    statsLoading: statsQuery.isLoading,

    // Memberships
    memberships: membershipsQuery.data ?? [],
    membershipsLoading: membershipsQuery.isLoading,

    // Upcoming bookings
    upcomingBookings: upcomingBookingsQuery.data ?? [],
    upcomingBookingsLoading: upcomingBookingsQuery.isLoading,

    // Paginated hooks (call with page number)
    useBookingHistory,
    usePaymentHistory,

    // Notes
    notes: notesQuery.data ?? [],
    notesLoading: notesQuery.isLoading,
    addNote: addNoteMutation,

    // Wallet
    walletBalance: walletQuery.data ?? 0,
    walletLoading: walletQuery.isLoading,

    // Punch cards
    punchCards: punchCardsQuery.data ?? [],

    // Mutations
    toggleBlock: toggleBlockMutation,
    deleteCustomer: deleteCustomerMutation,
  };
}
